// AF-3.16 — Opportunity Portfolio.
//
// AF UX audit P1C slice (docs/product/intelligence-console-ux-audit.md,
// Section 7) -- this page is migrated to the shared IntelligenceCard +
// LineageStrip pattern already proven on Recommendations (P0), Signals
// (P1), and Diagnoses (P1B). See
// docs/product/martian-intelligence-console-p1c-opportunities-report.md
// for the full report, including the deliberate, source-driven field
// mapping decisions noted inline below.
//
// Sources AF-3.11's own live get_opportunities_view() -- the real
// NOW/NEXT/INVESTIGATE/BLOCKED/WAITING classification, never re-ranked or
// recomputed client-side, and never inferred from any other field
// (confidence, priority, etc.). "Why now"/"why not now" text is enriched
// from AF-3.13's own why_now/why_not_now structures where available;
// never invented when absent.
//
// Field-mapping note (source of truth = the live OpportunityService
// document, `backend/services.py`'s `_upsert_opportunity`, confirmed by
// direct read, not the audit's illustrative wording): a real opportunity
// document also carries `title`, `description`, `status`, `severity`, and
// `estimated_impact` fields that this page has never rendered (only
// `opportunity_type`, `priority`, `impact_dimension`, and `confidence`
// were previously used) -- and does NOT carry any `readiness` field at
// all (the pre-migration OpportunityCard.jsx's `readiness` prop was
// already dead code; nothing ever passed it). Per this task's own
// "no new functionality, pure presentation reshuffle" scope, this
// migration surfaces exactly the fields the page already rendered before
// -- it does not newly expose `title`/`description`/`status`/`severity`/
// `estimated_impact`, and does not fabricate a `readiness` value. Adding
// any of those is a legitimate future enhancement, but is new information
// exposure, not a presentation change, so it is left for a separate,
// explicitly-scoped task.
//
// Lineage note: an opportunity's real `source_signal_ids`/
// `source_diagnosis_ids` are IDs only -- this page has never fetched the
// signals/diagnoses lists needed to resolve them into names (unlike
// Recommendations/Signals/Diagnoses, which already fetched their sibling
// lists for other reasons before their own migrations). Adding that fetch
// here would be new data-fetching behavior, not a pure presentation
// change, so the LineageStrip below surfaces the real, already-available
// COUNT of each real provenance array instead of an invented name -- the
// exact same raw IDs remain fully inspectable via the pre-existing,
// unchanged EvidenceDrawer's own provenance chips, opened the same way as
// before this migration.
//
// P2.5: still starts on the "brand" entity type (see OPPORTUNITY_PORTFOLIO_ENTITY_TYPES).
const OPPORTUNITY_PORTFOLIO_ENTITY_TYPES = ["brand", "product", "campaign", "creative", "channel", "audience"];

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '@/components/DashboardLayout';
import { BrandContextBar } from '@/components/brand/BrandContextBar';
import { IntelligenceCard } from '@/components/brand/IntelligenceCard';
import { LineageStrip } from '@/components/brand/LineageStrip';
import { StatusBadge, ConfidenceIndicator } from '@/components/brand/Badges';
import { EvidenceDrawer } from '@/components/brand/EvidenceDrawer';
import { RecommendationBridgePanel } from '@/components/brand/RecommendationBridgePanel';
import { EmptyState, ErrorState, LoadingState } from '@/components/brand/StateViews';
import { useBrandContext } from '@/hooks/useBrandContext';
import { brandGrowthAPI, growthPlanAPI } from '@/lib/intelligenceApi';
import { ArrowRight } from '@phosphor-icons/react';

// P1 console polish (docs/product/intelligence-console-whole-chain-audit.md,
// P1 #3; docs/product/intelligence-console-p1-polish-report.md) --
// presentation-only adapter. A real Opportunity evidence item
// (OpportunityService._build_evidence, backend/services.py) is always
// shaped { type: "signal_evidence" | "diagnosis_evidence", signal_id |
// diagnosis_id, signal_type | classification, description }. EvidenceDrawer
// (a shared component also used, with a DIFFERENT real evidence shape, by
// several other pages -- BrandCommandCenterPage.js and the Creator pages
// among them) expects { source_type, source_id, statement, confidence }.
// This function only renames real, already-present fields to the names
// EvidenceDrawer already knows how to render -- it never invents a value.
// An Opportunity evidence item has no real per-item confidence field, so
// `confidence` is correctly left unset here, exactly as EvidenceDrawer's
// own existing `{e.confidence !== null && ... }` guard already expects
// for an object that doesn't have one. EvidenceDrawer.jsx itself is not
// modified; every other caller of it is therefore unaffected.
function toEvidenceDrawerItems(evidence) {
  return (Array.isArray(evidence) ? evidence : []).map((e) => ({
    source_type: e.type,
    source_id: e.signal_id || e.diagnosis_id,
    statement: e.description,
  }));
}

export default function OpportunityPortfolioPage() {
  // P2.5 -- start on the "brand" entity type: the founder-triggered
  // SoM -> Signal -> Opportunity chain always produces entity_type="brand"
  // opportunities, and 'product' (the shared default) has no entities for
  // most real clients, so the just-created opportunity was invisible
  // until a manual entity-type switch. The founder can still switch to
  // any of OPPORTUNITY_PORTFOLIO_ENTITY_TYPES.
  const ctx = useBrandContext({ initialEntityType: 'brand' });
  const navigate = useNavigate();
  const [view, setView] = useState(null);
  const [whyMap, setWhyMap] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);
  // Martian Bridge -- the opportunity currently open in the recommendation
  // -> action plan -> approval panel below. Independent of `selected`
  // (the existing, unrelated evidence drawer) so either can be open on
  // its own.
  const [bridgeOpportunity, setBridgeOpportunity] = useState(null);

  const load = useCallback(() => {
    if (!ctx.ready) return;
    setLoading(true); setError(null);
    brandGrowthAPI.opportunities(ctx.workspaceId, ctx.entityType, ctx.entityId)
      .then((r) => setView(r.data))
      .catch((e) => setError(e))
      .finally(() => setLoading(false));

    growthPlanAPI.create(ctx.workspaceId, { entity_type: ctx.entityType, entity_id: ctx.entityId })
      .then((r) => {
        const map = {};
        if (r.data.why_now) map._primary = r.data.why_now;
        (r.data.why_not_now || []).forEach((w) => { map[w.opportunity] = w; });
        setWhyMap(map);
      })
      .catch(() => setWhyMap({}));
  }, [ctx.ready, ctx.workspaceId, ctx.entityType, ctx.entityId]);

  useEffect(() => { load(); }, [load]);

  const groups = view ? [
    { key: 'NOW', label: 'NOW', ids: view.top_opportunity_ids },
    { key: 'NEXT', label: 'NEXT', ids: view.next_opportunity_ids },
    { key: 'INVESTIGATE', label: 'INVESTIGATE', ids: view.investigation_opportunity_ids },
    { key: 'BLOCKED', label: 'BLOCKED', ids: view.blocked_opportunity_ids },
    { key: 'WAITING', label: 'WAITING', ids: view.waiting_opportunity_ids },
  ] : [];

  // Real backend classification only -- never re-derived from confidence,
  // priority, or any other field. `whyFor` resolves EITHER the real
  // why_now text (NOW only, from the primary growth-plan objective) OR
  // the real why_not_now text (any bucket that has one, in practice
  // WAITING) -- whichever the backend actually returned; never both,
  // never neither invented.
  const whyFor = (oppId, classification) => {
    if (classification === 'NOW' && whyMap._primary) {
      return { label: 'Why now', text: whyMap._primary.objective_connection || whyMap._primary.risk_of_delay };
    }
    const w = whyMap[oppId];
    if (w) {
      return { label: 'Why not now', text: `${w.reason?.replace(/_/g, ' ')} — ${w.required_condition}` };
    }
    return null;
  };

  return (
    <DashboardLayout>
      <div data-testid="opportunity-portfolio-page" className="max-w-5xl mx-auto">
        <div className="mb-6">
          <p className="text-[11px] font-mono uppercase tracking-wide text-[var(--accent)] mb-1">Where could growth exist?</p>
          <h1 className="font-display text-[20px] font-bold text-[var(--text-primary)] mb-1">Opportunity Portfolio</h1>
          <p className="text-[12px] text-[var(--text-secondary)]">Every opportunity Martian has identified, ranked by real evidence -- never a bare score.</p>
        </div>

        <BrandContextBar ctx={ctx} entityTypes={OPPORTUNITY_PORTFOLIO_ENTITY_TYPES} />

        {loading && <LoadingState />}
        {error && <ErrorState error={error} onRetry={load} />}

        {!loading && !error && view && view.opportunities?.length === 0 && (
          <EmptyState title="NO OPPORTUNITIES YET" description="Martian has not identified any opportunities for this entity yet." />
        )}

        {!loading && !error && view && view.opportunities?.length > 0 && (
          <div className="space-y-6" data-testid="opportunity-groups">
            {groups.filter((g) => g.ids?.length > 0).map((g) => (
              <div key={g.key} data-testid={`opportunity-group-${g.key}`}>
                <p className="text-[11px] font-mono uppercase tracking-wide text-[var(--text-tertiary)] mb-2">{g.label} ({g.ids.length})</p>
                <div className="space-y-3">
                  {g.ids.map((oppId) => {
                    const opp = view.opportunities.find((o) => o.id === oppId);
                    if (!opp) return null;
                    const classification = view.classifications?.[oppId];
                    const why = whyFor(oppId, classification);
                    const evidenceCount = Array.isArray(opp.evidence) ? opp.evidence.length : 0;
                    const diagnosisCount = Array.isArray(opp.source_diagnosis_ids) ? opp.source_diagnosis_ids.length : 0;
                    const signalCount = Array.isArray(opp.source_signal_ids) ? opp.source_signal_ids.length : 0;

                    // Real provenance counts only -- the underlying IDs are
                    // already real (fetched as part of this same opportunity
                    // object), but this page has never fetched the
                    // signals/diagnoses lists needed to resolve them into
                    // names (see file header). A count of a real array is a
                    // real relationship signal, never a fabricated one; the
                    // exact IDs remain inspectable via the unchanged
                    // EvidenceDrawer opened below.
                    // P1 console polish (docs/product/intelligence-console-whole-chain-audit.md,
                    // P1 #2) -- clickable because the count itself comes
                    // from a real, non-empty provenance array on this
                    // same opportunity (source_diagnosis_ids/
                    // source_signal_ids) -- a genuine reference, even
                    // though (per the file header) no new fetch resolves
                    // it to a name. Navigates to the Diagnoses/Signals
                    // page itself, same non-deep-linked pattern as the
                    // pre-existing Signals -> Opportunity link.
                    const lineageItems = [];
                    if (diagnosisCount > 0) {
                      lineageItems.push({
                        label: 'From diagnosis',
                        value: `${diagnosisCount} hypothes${diagnosisCount === 1 ? 'is' : 'es'}`,
                        testId: 'lineage-diagnosis',
                        onClick: () => navigate('/growth/diagnoses'),
                      });
                    }
                    if (signalCount > 0) {
                      lineageItems.push({
                        label: 'From signal',
                        value: `${signalCount} signal${signalCount === 1 ? '' : 's'}`,
                        testId: 'lineage-signal',
                        onClick: () => navigate('/growth/signals'),
                      });
                    }

                    return (
                      <IntelligenceCard
                        key={oppId}
                        testId={`opportunity-card-${oppId}`}
                        stage="OPPORTUNITY"
                        title={opp.opportunity_type?.replace(/_/g, ' ') || 'Opportunity'}
                        badges={classification && <StatusBadge value={classification} />}
                        statementLabel={why?.label}
                        statement={why?.text}
                        metrics={[
                          opp.priority ? { label: 'Priority', value: opp.priority } : null,
                          opp.impact_dimension ? { label: 'Impact', value: opp.impact_dimension } : null,
                          opp.confidence !== undefined && opp.confidence !== null
                            ? { label: 'Confidence', value: <ConfidenceIndicator value={opp.confidence} />, testId: 'intelligence-card-confidence' }
                            : null,
                        ].filter(Boolean)}
                        lineage={(
                          <>
                            {evidenceCount > 0 && (
                              <p className="text-[11px] font-medium text-[var(--text-secondary)] mb-2" data-testid="opportunity-evidence-count">
                                {evidenceCount} evidence item{evidenceCount === 1 ? '' : 's'} recorded
                              </p>
                            )}
                            <LineageStrip items={lineageItems} />
                          </>
                        )}
                        decision={(
                          <div className="flex items-center gap-3 flex-wrap">
                            <button
                              type="button"
                              data-testid="opportunity-view-evidence"
                              onClick={() => setSelected(opp)}
                              className="inline-flex items-center gap-1 rounded-[var(--radius-button,6px)] border border-[var(--border)] bg-[var(--surface-sunken,var(--canvas-secondary))] px-2.5 py-1 text-[11px] font-medium text-[var(--text-primary)] hover:bg-[var(--neutral-bg)]"
                            >
                              View evidence →
                            </button>
                            {/* Martian Bridge -- the missing INSIGHT -> ACTION
                                 link: every other control on this page only
                                 ever reads (evidence, why-now/why-not-now).
                                 This is the one write-capable entry point
                                 into the real recommendation/action-plan/
                                 approval chain for this opportunity --
                                 same testid, same handler, same panel as
                                 before this migration. */}
                            <button
                              type="button"
                              data-testid="opportunity-view-recommendations"
                              onClick={() => setBridgeOpportunity(opp)}
                              className="inline-flex items-center gap-1 text-[11px] font-medium text-[var(--accent)] hover:opacity-80"
                            >
                              Recommendations &amp; action plan <ArrowRight size={11} />
                            </button>
                          </div>
                        )}
                      />
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}

        {selected && (
          <EvidenceDrawer
            open={!!selected}
            onOpenChange={(o) => !o && setSelected(null)}
            title={selected.opportunity_type?.replace(/_/g, ' ') || 'Opportunity'}
            evidence={toEvidenceDrawerItems(selected.evidence)}
            provenance={{ source_signal_ids: selected.source_signal_ids, source_diagnosis_ids: selected.source_diagnosis_ids, source_knowledge_ids: selected.source_knowledge_ids }}
          />
        )}

        {bridgeOpportunity && (
          <RecommendationBridgePanel
            open={!!bridgeOpportunity}
            onOpenChange={(o) => !o && setBridgeOpportunity(null)}
            workspaceId={ctx.workspaceId}
            opportunity={bridgeOpportunity}
          />
        )}
      </div>
    </DashboardLayout>
  );
}
